import { useState, useCallback, useEffect, useMemo } from 'react'
import {
    Modal,
    ModalDialog,
    ModalClose,
    Typography,
    Divider,
    LinearProgress,
    Stack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Chip,
    Alert,
    IconButton,
    Sheet,
    Switch,
    Select,
    Option,
} from '@mui/joy'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

import { getGroup, updateGroup, addAccountToGroup, removeAccountFromGroup } from '../../../api/telegramApi'
import { type TelegramAccount, type TelegramGroup } from '../types'

interface ManageGroupModalProps {
    open: boolean
    peerId: string
    accounts: TelegramAccount[]
    onClose: () => void
    onUpdate: () => void
}

export function ManageGroupModal({ open, peerId, accounts, onClose, onUpdate }: ManageGroupModalProps): JSX.Element {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [group, setGroup] = useState<TelegramGroup | null>(null)
    const [originalName, setOriginalName] = useState('')
    const [brokerCompanyName, setBrokerCompanyName] = useState('')
    const [joinLink, setJoinLink] = useState('')
    const [status, setStatus] = useState<'active' | 'inactive'>('active')
    const [modalMessage, setModalMessage] = useState<string | null>(null)
    const [modalError, setModalError] = useState<string | null>(null)
    const [selectedAccountToAdd, setSelectedAccountToAdd] = useState<string | null>(null)
    const [addingAccount, setAddingAccount] = useState(false)
    const [removingAccountPhone, setRemovingAccountPhone] = useState<string | null>(null)

    const resetModalAlerts = useCallback(() => {
        setModalMessage(null)
        setModalError(null)
    }, [])

    useEffect(() => {
        if (modalMessage || modalError) {
            const timer = setTimeout(resetModalAlerts, 5000)
            return () => clearTimeout(timer)
        }
    }, [modalMessage, modalError, resetModalAlerts])

    useEffect(() => {
        if (!open || !peerId) return

        const loadGroup = async () => {
            resetModalAlerts()
            setLoading(true)
            try {
                const groupData = await getGroup(peerId)
                setGroup(groupData)
                setOriginalName(groupData.originalName || '')
                setBrokerCompanyName(groupData.brokerCompanyName || '')
                setJoinLink(groupData.joinLink || '')
                setStatus(groupData.status || 'active')
            } catch (e) {
                setModalError((e as Error)?.message || 'Failed to load group details')
            } finally {
                setLoading(false)
            }
        }

        void loadGroup()
    }, [open, peerId, resetModalAlerts])

    const watchingAccounts = useMemo(() => group?.watchingAccounts ?? [], [group])

    const availableAccountsToAdd = useMemo(() => {
        const watchingPhones = new Set(watchingAccounts.map((a) => a.phone))
        return accounts.filter((a) => !watchingPhones.has(a.phone))
    }, [accounts, watchingAccounts])

    const handleSave = useCallback(async () => {
        if (!peerId) return
        setSaving(true)
        resetModalAlerts()
        try {
            await updateGroup(peerId, {
                originalName: originalName.trim() || null,
                brokerCompanyName: brokerCompanyName.trim() || null,
                joinLink: joinLink.trim() || null,
                status,
            })
            setModalMessage('Group updated successfully')
            onUpdate()
        } catch (e) {
            setModalError((e as Error)?.message || 'Failed to update group')
        } finally {
            setSaving(false)
        }
    }, [peerId, originalName, brokerCompanyName, joinLink, status, onUpdate, resetModalAlerts])

    const handleAddAccount = useCallback(async () => {
        if (!selectedAccountToAdd || !peerId) return
        setAddingAccount(true)
        resetModalAlerts()
        try {
            await addAccountToGroup(peerId, selectedAccountToAdd)
            setModalMessage(`Account ${selectedAccountToAdd} added to group`)
            setSelectedAccountToAdd(null)
            const groupData = await getGroup(peerId)
            setGroup(groupData)
            onUpdate()
        } catch (e) {
            setModalError((e as Error)?.message || 'Failed to add account')
        } finally {
            setAddingAccount(false)
        }
    }, [peerId, selectedAccountToAdd, onUpdate, resetModalAlerts])

    const handleRemoveAccount = useCallback(
        async (phone: string) => {
            if (!peerId) return
            setRemovingAccountPhone(phone)
            resetModalAlerts()
            try {
                await removeAccountFromGroup(peerId, phone)
                setModalMessage(`Account ${phone} removed from group`)
                const groupData = await getGroup(peerId)
                setGroup(groupData)
                onUpdate()
            } catch (e) {
                setModalError((e as Error)?.message || 'Failed to remove account')
            } finally {
                setRemovingAccountPhone(null)
            }
        },
        [peerId, onUpdate, resetModalAlerts]
    )

    const handleModalClose = useCallback(() => {
        setGroup(null)
        setOriginalName('')
        setBrokerCompanyName('')
        setJoinLink('')
        setStatus('active')
        setSelectedAccountToAdd(null)
        resetModalAlerts()
        onClose()
    }, [onClose, resetModalAlerts])

    return (
        <Modal open={open} onClose={handleModalClose}>
            <ModalDialog variant="outlined" sx={{ borderRadius: 'lg', minWidth: 600, maxWidth: 700, maxHeight: '90vh' }}>
                <ModalClose />
                <Typography level="h4" component="h2">
                    Manage Group
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Peer ID: {peerId}
                </Typography>
                <Divider sx={{ my: 2 }} />

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {modalMessage && (
                    <Alert
                        color="success"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="success" onClick={resetModalAlerts}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalMessage}
                    </Alert>
                )}
                {modalError && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={resetModalAlerts}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalError}
                    </Alert>
                )}

                {!loading && group && (
                    <Stack spacing={2} sx={{ overflow: 'auto' }}>
                        {/* Edit Form */}
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                            <Typography level="title-sm" sx={{ mb: 2 }}>
                                Group Details
                            </Typography>
                            <Stack spacing={2}>
                                <FormControl>
                                    <FormLabel>Original Name</FormLabel>
                                    <Input placeholder="Enter original name" value={originalName} onChange={(e) => setOriginalName(e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Broker Company Name</FormLabel>
                                    <Input
                                        placeholder="Enter broker company name"
                                        value={brokerCompanyName}
                                        onChange={(e) => setBrokerCompanyName(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Join Link</FormLabel>
                                    <Input placeholder="https://t.me/..." value={joinLink} onChange={(e) => setJoinLink(e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography level="body-sm">Inactive</Typography>
                                        <Switch checked={status === 'active'} onChange={(e) => setStatus(e.target.checked ? 'active' : 'inactive')} />
                                        <Typography level="body-sm">Active</Typography>
                                    </Stack>
                                </FormControl>
                            </Stack>
                        </Sheet>

                        {/* Watching Accounts */}
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                            <Typography level="title-sm" sx={{ mb: 2 }}>
                                Watching Accounts ({watchingAccounts.length})
                            </Typography>
                            {watchingAccounts.length === 0 ? (
                                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                    No accounts watching this group
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {watchingAccounts.map((account) => (
                                        <Stack
                                            key={account.phone}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ p: 1, bgcolor: 'background.level1', borderRadius: 'sm' }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography level="body-sm">{account.phone}</Typography>
                                                <Chip size="sm" variant="soft" color={account.status === 'active' ? 'success' : 'warning'}>
                                                    {account.status}
                                                </Chip>
                                            </Stack>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={() => handleRemoveAccount(account.phone)}
                                                disabled={removingAccountPhone === account.phone}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}

                            {/* Add Account */}
                            <Divider sx={{ my: 1 }} />
                            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                                Add Account to Watch
                            </Typography>
                            {availableAccountsToAdd.length > 0 ? (
                                <Stack direction="row" spacing={1}>
                                    <Select
                                        placeholder="Select account to add"
                                        value={selectedAccountToAdd}
                                        onChange={(_, value) => setSelectedAccountToAdd(value)}
                                        sx={{ flex: 1 }}
                                        disabled={addingAccount}
                                    >
                                        {availableAccountsToAdd.map((account) => (
                                            <Option key={account.phone} value={account.phone}>
                                                {account.phone} ({account.status})
                                            </Option>
                                        ))}
                                    </Select>
                                    <Button
                                        variant="solid"
                                        startDecorator={<AddIcon />}
                                        onClick={handleAddAccount}
                                        disabled={!selectedAccountToAdd}
                                        loading={addingAccount}
                                    >
                                        Add
                                    </Button>
                                </Stack>
                            ) : (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                    {accounts.length === 0
                                        ? 'No accounts available. Add a Telegram account first.'
                                        : 'All accounts are already watching this group.'}
                                </Typography>
                            )}
                        </Sheet>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={handleModalClose}>
                                Cancel
                            </Button>
                            <Button variant="solid" color="primary" onClick={handleSave} loading={saving}>
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    )
}
